/**
 * Random Generation Utilities
 * Optimized functions for generating random Pokémon selections
 */

/**
 * Generates a cryptographically strong random integer between min and max (inclusive)
 * Uses crypto.getRandomValues for better randomness
 */
const randomInt = (min: number, max: number): number => {
  const range = max - min + 1;
  const bytesNeeded = Math.ceil(Math.log2(range) / 8);
  const maxValue = Math.pow(256, bytesNeeded);
  const randomBytes = new Uint8Array(bytesNeeded);

  let randomValue: number;
  do {
    crypto.getRandomValues(randomBytes);
    randomValue = randomBytes.reduce((acc, byte, i) => acc + byte * Math.pow(256, i), 0);
  } while (randomValue >= maxValue - (maxValue % range));

  return min + (randomValue % range);
};

/**
 * Generates an array of unique random Pokémon IDs
 * Optimized with Set for O(1) lookups
 */
export const generateRandomPokemonIds = (count: number, maxId: number): number[] => {
  if (count > maxId) {
    throw new Error('Cannot generate more unique IDs than maxId');
  }

  const ids = new Set<number>();

  // Use a more efficient algorithm for large counts
  if (count > maxId / 2) {
    // Generate all IDs and shuffle, then take first 'count'
    const allIds = Array.from({ length: maxId }, (_, i) => i + 1);
    return shuffle(allIds).slice(0, count);
  }

  // For small counts, keep generating random IDs
  while (ids.size < count) {
    ids.add(randomInt(1, maxId));
  }

  return Array.from(ids);
};

/**
 * Shuffles an array using Fisher-Yates algorithm
 * In-place for better performance
 */
export const shuffle = <T>(array: T[]): T[] => {
  const shuffled = [...array];

  for (let i = shuffled.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [shuffled[i], shuffled[j]] = [shuffled[j], shuffled[i]];
  }

  return shuffled;
};
